/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/useQaPhoneUi.ts
 * FILE_ID: JOBHUB-QA-PHONE-UI-001
 * PURPOSE: One open-id for phone sheets plus the expanded row. Mode stays in workspace.
 */
import { useCallback, useState } from 'react'

export type QaPhoneSheetId =
  | 'more'
  | 'scope-area'
  | 'scope-checklist'
  | 'scope-quote'
  | 'notes'
  | 'assign'
  | 'status'
  | 'row-actions'
  | 'move'
  | 'new-checklist'
  | 'pack'
  | 'density'
  | 'confirm-delete'
  | 'confirm-quote'
  | 'prepare'
  | 'templates'
  | 'select-qa'
  | 'add-product'
  | 'standard-checks'
  | 'belongs-to-level'
  | 'add'
  | 'itp-library'

export type QaBuildView = 'cards' | 'table'

export function qaPhoneRenameRoute(
  view: QaBuildView,
  checklistId: string | null,
  tableIds: readonly string[],
): { table: string | null; sheet: string | null; drop: boolean } {
  if (!checklistId) return { table: null, sheet: null, drop: false }
  if (view !== 'table') return { table: null, sheet: checklistId, drop: false }
  return tableIds.includes(checklistId)
    ? { table: checklistId, sheet: null, drop: false }
    : { table: null, sheet: null, drop: true }
}

const BUILD_VIEW_KEY = 'qt.jobhub.qa.phone.buildView'

/** Cards are the phone default: the table needs ~640px of columns, which on a
 *  phone is a horizontal scroll with every field but the first off-screen. */
function readBuildView(): QaBuildView {
  if (typeof window === 'undefined') return 'cards'
  try {
    return window.localStorage.getItem(BUILD_VIEW_KEY) === 'table' ? 'table' : 'cards'
  } catch {
    return 'cards'
  }
}

export function useQaPhoneUi() {
  const [openSheet, setOpenSheet] = useState<QaPhoneSheetId | null>(null)
  const [buildView, setBuildViewState] = useState<QaBuildView>(readBuildView)
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)
  const [selecting, setSelecting] = useState(false)
  const closeSheet = useCallback(() => setOpenSheet(null), [])
  const toggleSheet = useCallback(
    (id: QaPhoneSheetId) => setOpenSheet((current) => (current === id ? null : id)),
    [],
  )
  const toggleExpandedRow = useCallback(
    (id: string) => setExpandedRowId((current) => (current === id ? null : id)),
    [],
  )
  const setBuildView = useCallback((next: QaBuildView) => {
    setBuildViewState(next)
    try {
      window.localStorage.setItem(BUILD_VIEW_KEY, next)
    } catch {
      /* storage unavailable (private mode) — the choice stays in-memory */
    }
  }, [])
  const toggleBuildView = useCallback(
    () => setBuildView(buildView === 'cards' ? 'table' : 'cards'),
    [buildView, setBuildView],
  )

  return {
    buildView,
    setBuildView,
    toggleBuildView,
    openSheet,
    setOpenSheet,
    closeSheet,
    toggleSheet,
    expandedRowId,
    setExpandedRowId,
    toggleExpandedRow,
    selecting,
    setSelecting,
  }
}

export type QaPhoneUi = ReturnType<typeof useQaPhoneUi>
